// =============================================
// System Admin Dashboard Page
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Building2, Baby, Landmark, TrendingUp } from 'lucide-react';
import { adminApi, type AdminDashboardStats } from '../../services/api';
import { useAdminStore } from '../../stores/adminStore';
import { sysAdminColors as colors } from '../../styles/colors';

export default function SysAdminDashboard() {
  const { selectedOrganizationId } = useAdminStore();
  const orgFilter = selectedOrganizationId || undefined;

  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const result = await adminApi.getDashboard(orgFilter);
        if (result.success && result.data) {
          setStats(result.data);
        }
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, [orgFilter]);

  const statCards = stats ? [
    { label: 'ארגונים', value: stats.totalOrganizations.toString(), icon: Landmark, color: colors.primary, bgColor: colors.primaryLight },
    { label: 'גנים פעילים', value: stats.totalKindergartens.toString(), icon: Building2, color: colors.secondary, bgColor: colors.secondaryLight },
    { label: 'עובדים', value: stats.totalStaff.toString(), icon: Users, color: colors.success, bgColor: colors.successLight },
    { label: 'ילדים רשומים', value: stats.totalChildren.toString(), icon: Baby, color: colors.warning, bgColor: colors.warningLight },
  ] : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
          לוח בקרה - מנהל מערכת
        </h1>
        <p style={{ fontSize: '14px', color: colors.textLight }}>
          סקירה כללית של המערכת
        </p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען נתונים...
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
        }}>
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '4px' }}>
                    {stat.label}
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: stat.color }}>
                    {stat.value}
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: stat.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <stat.icon size={24} color={stat.color} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={20} color={colors.primary} />
          פעולות מהירות
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          {[
            { label: 'הקמת ארגון חדש', href: '/sysadmin/organizations', color: colors.primary },
            { label: 'הוספת עובד', href: '/sysadmin/staff', color: colors.secondary },
            { label: 'ניהול גנים', href: '/sysadmin/kindergartens', color: colors.success },
          ].map((action) => (
            <a
              key={action.label}
              href={action.href}
              style={{
                display: 'block',
                padding: '14px 16px',
                borderRadius: '10px',
                border: `1px solid ${colors.primary}20`,
                backgroundColor: `${action.color}08`,
                color: action.color,
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'center',
                transition: 'all 0.2s',
              }}
            >
              {action.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
